import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useQueryState } from "react-router-use-location-state";
import Alert from "@material-ui/lab/Alert";
import {
  Tabs,
  Tab,
  NavLink,
  LinearProgress,
  Heading,
  Button,
} from "../../components";
import { Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { useRouteMatch } from "react-router-dom";
import TaskDetails from "./TaskDetails";
import ExecutionSummary from "./ExecutionSummary";
import ExecutionJson from "./ExecutionJson";
import InputOutput from "./ExecutionInputOutput";
import clsx from "clsx";
import ActionModule from "./ActionModule";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import RefreshIcon from "@material-ui/icons/Refresh";
import TimerIcon from "@material-ui/icons/Timer";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import RightPanel from "./RightPanel";
import WorkflowDAG from "../../components/diagram/WorkflowDAG";
import StatusBadge from "../../components/StatusBadge";
import { Helmet } from "react-helmet";
import sharedStyles from "../styles";
import rison from "rison";
import { useWorkflow } from "../../data/workflow";
import { ArrowBack } from "@material-ui/icons";
import { usePushHistory } from "../../components/NavLink";
import WolkenSpinner from "../../components/WolkenSpinner";
import DefaultLoader from "../../components/DefaultLoader";
import { getParentUrl } from "../../utils/helpers";

const maxWindowWidth = window.innerWidth;
const INIT_DRAWER_WIDTH = 650;
// const TICKET_SUMMARY_INIT_DRAWER_WIDTH = 450;
const TICKET_SUMMARY_INIT_DRAWER_WIDTH = "50%";

const useStyles = makeStyles({
  header: sharedStyles.header,
  drawer: {
    zIndex: 999,
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    // width: (state) => (state.isFullWidth ? "100%" : state.drawerWidth),
    width: (state) => (state.isFullWidth ? "100%" : state.drawerWidth),
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: 10,
    justifyContent: "flex-end",
    // height: 80,
    height: 40,
    flexShrink: 0,
    boxShadow: "0 4px 8px 0 rgb(0 0 0 / 10%), 0 0 2px 0 rgb(0 0 0 / 10%)",
    zIndex: 1,
    backgroundColor: "#fff",
  },
  dragger: {
    display: (state) => (state.isFullWidth ? "none" : "block"),
    width: "5px",
    cursor: "ew-resize",
    padding: "4px 0 0",
    position: "absolute",
    height: "100%",
    zIndex: "100",
    backgroundColor: "#f4f7f9",
  },
  drawerMain: {
    paddingLeft: (state) => (state.isFullWidth ? 0 : 4),
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  drawerContent: {
    flex: 1,
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  contentShift: {
    marginRight: (state) => state.drawerWidth,
  },
  tabContent: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  headerSubtitle: {
    marginBottom: 10,
    fontSize: 12,
  },
  fr: {
    display: "flex",
    position: "relative",
    float: "right",
    // marginRight: 50,
    marginTop: 30,
    zIndex: 1,
  },
  frItem: {
    display: "flex",
    alignItems: "center",
    marginRight: 15,
  },
  rightPanel: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  body: {
    width: "100vw",
    height: "100vh",
  },
  flexBody: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
});

export default function Execution(props) {
  const permissions = props.permissions;
  const match = useRouteMatch();
  const navigate = usePushHistory();
  const { workflowID, view, rightDrawerWidth } = props;

  const { data: execution, isFetching, refetch: refresh } = useWorkflow(
    !view ? match.params.id : workflowID
  );

  const [isFullWidth, setIsFullWidth] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(
    workflowID ? TICKET_SUMMARY_INIT_DRAWER_WIDTH : INIT_DRAWER_WIDTH
  );

  const [tabIndex, setTabIndex] = useQueryState("tabIndex", 0);
  const [selectedTaskRison, setSelectedTaskRison] = useQueryState("task", "");
  
  const [refreshInterval, setRefreshInterval] = useState(0); // 0 means manual refresh
  const [refreshIntervalId, setRefreshIntervalId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [countdownSeconds, setCountdownSeconds] = useState(null);
  const [prevExecutionStatus, setPrevExecutionStatus] = useState(null);

  const dag = useMemo(() => {
    if (execution) {
      return new WorkflowDAG(execution, null, view);
    } else {
      return null;
    }
  }, [execution, view]);

  const selectedTask = useMemo(
    () => selectedTaskRison && rison.decode(selectedTaskRison),
    [selectedTaskRison]
  );

  const setSelectedTask = (taskPointer) => {
    setSelectedTaskRison(rison.encode(taskPointer));
  };

  const deselectTaskAndCloseRightPanel = () => {
    setSelectedTaskRison(null);
    handleClose();
  };

  const classes = useStyles({
    isFullWidth,
    drawerWidth,
  });

  const handleMousemove = useCallback(
    (e) => {
      // we don't want to do anything if we aren't resizing.
      if (!isResizing) {
        return;
      }

      // Stop highlighting
      e.preventDefault();
      const offsetRight =
        document.body.offsetWidth - (e.clientX - document.body.offsetLeft);
      const minWidth = 0;
      const maxWidth = maxWindowWidth - 100;

      if (offsetRight > minWidth && offsetRight < maxWidth) {
        setDrawerWidth(offsetRight);
      }
    },
    [isResizing]
  );

  const handleMousedown = (e) => setIsResizing(true);
  const handleDragEnd = (e) => {
    const rightDrawer = document.getElementById("slide-panel");
    if (rightDrawer) setDrawerWidth(rightDrawer.clientWidth);
  };

  const handleClose = () => {
    setSelectedTaskRison(null);
  };

  const handleFullScreen = () => {
    setIsFullWidth(true);
  };

  const handleFullScreenExit = () => {
    setIsFullWidth(false);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRefreshIntervalChange = (interval) => {
    // Clear any existing interval
    if (refreshIntervalId) {
      clearInterval(refreshIntervalId);
      setRefreshIntervalId(null);
    }

    // Set the new interval
    setRefreshInterval(interval);

    // If interval is greater than 0, create a new interval with countdown. Interval 0 means Manual Refresh
    if (interval > 0) {
      // Initialize countdown to match interval
      setCountdownSeconds(interval);

      const id = setInterval(() => {
        // Check if workflow is in terminal state before decrementing
      if (execution &&
          (execution.status === "COMPLETED" ||
           execution.status === "FAILED" ||
           execution.status === "TERMINATED" ||
           execution.status === "TIMED_OUT" ||
           execution.status === "PAUSED")) {
          // Clear the timer if workflow reached terminal state
          clearInterval(id);
          setRefreshIntervalId(null);
          setRefreshInterval(0);
          setCountdownSeconds(null);
          return;
        }

        setCountdownSeconds(prev => {

          const newCount = prev - 1;

          // When countdown reaches 0, refresh and reset
          if (newCount <= 0) {
            handleClose();
            refresh();
            // Reset countdown after refresh
            return interval;
          }
          return newCount;
        });
      }, 1000);

      setRefreshIntervalId(id);
    } else {
      // For manual refresh, no countdown needed
      setCountdownSeconds(null);
    }
  };

  // Create a more robust effect to detect workflow status changes
  useEffect(() => {
    // Only proceed if we have an execution
    if (!execution) return;

  const isTerminalState =
    execution.status === "COMPLETED" ||
    execution.status === "FAILED" ||
    execution.status === "TERMINATED" ||
    execution.status === "TIMED_OUT" ||
    execution.status === "PAUSED";

  const wasTerminalState =
    prevExecutionStatus === "COMPLETED" ||
    prevExecutionStatus === "FAILED" ||
    prevExecutionStatus === "TERMINATED" ||
    prevExecutionStatus === "TIMED_OUT" ||
    prevExecutionStatus === "PAUSED";

    // Check for transition to terminal state (from non-terminal to terminal)
    if (isTerminalState && !wasTerminalState) {

      // Always cancel any auto-refresh when workflow completes
      if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
        setRefreshIntervalId(null);
        setRefreshInterval(0);
        setCountdownSeconds(null);
      }

      // Do one final refresh to ensure we have the latest data
      handleClose();
      refresh();
    }

    // Update the previous status for next comparison
    setPrevExecutionStatus(execution.status);

  }, [execution, prevExecutionStatus]);

  // Keep the cleanup effect
  useEffect(() => {
    return () => {
      if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
      }
    };
  }, [refreshIntervalId]);



  // On load and destroy only
  useEffect(() => {
    const mouseUp = (e) => setIsResizing(false);

    document.addEventListener("mousemove", handleMousemove);
    document.addEventListener("mouseup", mouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMousemove);
      document.removeEventListener("mouseup", mouseUp);
    };
  }, [handleMousemove]);

  useEffect(() => {
    // on load close right pannel
    handleClose();

    return () => {};
  }, []);

  return (
    <>
      {!workflowID ? (
        <>
          <Helmet>
            <title>Conductor UI - Execution - {match?.params?.id ?? ""}</title>
          </Helmet>
          <div
            className={clsx(classes.content, {
              [classes.contentShift]: !!selectedTask,
            })}>
            {isFetching && <LinearProgress />}
            {execution && (
              <>
                <div className={classes.header} id="exe-header">
                  <div className={classes.fr}>
                    {execution.parentWorkflowId && (
                      <div className={classes.frItem}>
                        <NavLink
                          absolutePath
                          newTab
                          // samePage
                          path={`https://${getParentUrl()}/wolken/esd/w_flow?flowRedirectUrl=execution/${
                            execution.parentWorkflowId
                          }`}>
                          Parent Workflow
                        </NavLink>
                      </div>
                    )}
                    <div className={classes.frItem}>
                      <NavLink
                        absolutePath
                        newTab
                        // samePage
                        path={`https://${getParentUrl()}/wolken/esd/w_flow?flowRedirectUrl=/workflowDef/${
                          execution.workflowName
                        }`}>
                        Definition
                      </NavLink>
                    </div>
                    {/* <Button
                      onClick={refresh}
                      style={{ marginRight: 10 }} color="primary" variant="outlined">
                      Refresh
                    </Button> */}

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title={
                        execution &&
                        (execution.status === "COMPLETED" ||
                         execution.status === "FAILED" ||
                         execution.status === "TERMINATED")
                          ? `Workflow ${execution.status.toLowerCase()}`
                          : refreshInterval === 0
                            ? 'Refresh'
                            : `Auto-refresh in ${countdownSeconds}s`
                      }>
                        <span> {/* Wrap in span to allow tooltip on disabled button */}
                          <IconButton
                            onClick={() => {
                              // Always perform a refresh when clicked (if not in terminal state)
                              if (!(execution &&
                                   (execution.status === "COMPLETED" ||
                                    execution.status === "FAILED" ||
                                    execution.status === "TERMINATED"))) {
                                handleClose();
                                refresh();

                                // If auto-refresh is active, reset the countdown
                                if (refreshInterval > 0) {
                                  // Reset the countdown to the full interval
                                  setCountdownSeconds(refreshInterval);
                                }
                              }
                            }}
                            disabled={
                              execution &&
                              (execution.status === "COMPLETED" ||
                               execution.status === "FAILED" ||
                               execution.status === "TERMINATED")
                            }
                            style={{ marginRight: 0, position: 'relative' }}>
                            {refreshInterval === 0 ? (
                              <RefreshIcon />
                            ) : (
                              <>
                                <TimerIcon />
                                <div style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                  background: '#2196f3',
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '16px',
                                  height: '16px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {countdownSeconds}
                                </div>
                              </>
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton
                        onClick={handleMenuOpen}
                        size="small"
                        disabled={
                          execution &&
                          (execution.status === "COMPLETED" ||
                           execution.status === "FAILED" ||
                           execution.status === "TERMINATED")
                        }
                        style={{ marginRight: 10 }}>
                        <ArrowDropDownIcon />
                      </IconButton>
                      <Menu
                        id="refresh-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}>
                        <MenuItem
                          selected={refreshInterval === 0}
                          onClick={() => {
                            handleRefreshIntervalChange(0);
                            handleMenuClose();
                          }}>
                          Manual Refresh
                        </MenuItem>
                        <MenuItem
                          selected={refreshInterval === 3}
                          onClick={() => {
                            handleRefreshIntervalChange(3);
                            handleMenuClose();
                          }}>
                          Auto-refresh every 3s
                        </MenuItem>
                        <MenuItem
                          selected={refreshInterval === 5}
                          onClick={() => {
                            handleRefreshIntervalChange(5);
                            handleMenuClose();
                          }}>
                          Auto-refresh every 5s
                        </MenuItem>
                        <MenuItem
                          selected={refreshInterval === 7}
                          onClick={() => {
                            handleRefreshIntervalChange(7);
                            handleMenuClose();
                          }}>
                          Auto-refresh every 7s
                        </MenuItem>
                      </Menu>
                    </div>

                    { permissions?.workflows?.execution_actions=='True' &&
                    <ActionModule
                      execution={execution}
                      // triggerReload={refresh}
                      triggerReload={() => {
                        handleClose();
                        refresh();
                      }}
                    />}
                  </div>

                  {/* <Heading level="subtitle1" gutterBottom></Heading> */}
                  <NavLink
                    path={`/`}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}>
                    <ArrowBack fontSize="medium" /> Back
                  </NavLink>
                  <Heading level={0} gutterBottom>
                    {execution.workflowType || execution.workflowName}{" "}
                    <StatusBadge status={execution.status} />
                  </Heading>
                  <Heading level={-1} className={classes.headerSubtitle}>
                    {execution.workflowId}
                  </Heading>

                  {execution.reasonForIncompletion && (
                    <Alert
                      severity={
                        execution.status === "COMPLETED"
                          ? "success"
                          : execution.status === "FAILED"
                          ? "error"
                          : ""
                      }>
                      {execution.reasonForIncompletion}
                    </Alert>
                  )}

                  <Tabs
                    value={tabIndex}
                    style={{ marginBottom: 0 }}
                    indicatorColor="primary"
                    textColor="primary">
                    <Tab label="Tasks" onClick={() => setTabIndex(0)} />
                    <Tab label="Summary" onClick={() => setTabIndex(1)} />
                    <Tab
                      label="Workflow Input/Output"
                      onClick={() => setTabIndex(2)}
                    />
                    <Tab label="JSON" onClick={() => setTabIndex(3)} />
                  </Tabs>
                </div>
                <div className={classes.tabContent}>
                  {tabIndex === 0 && (
                    <TaskDetails
                      dag={dag}
                      execution={execution}
                      setSelectedTask={setSelectedTask}
                      selectedTask={selectedTask}
                      refresh={refresh}
                      rightDrawerWidth={drawerWidth}
                      isFetching={isFetching}
                      deselectTaskAndCloseRightPanel={
                        deselectTaskAndCloseRightPanel
                      }
                    />
                  )}
                  {tabIndex === 1 && <ExecutionSummary execution={execution} />}
                  {tabIndex === 2 && <InputOutput execution={execution} />}
                  {tabIndex === 3 && <ExecutionJson execution={execution} />}
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          {/* if id available through props show just task */}
          {execution && !isFetching ? (
            <div style={{ overflow: "hidden" }}>
              <TaskDetails
                dag={dag}
                execution={execution}
                setSelectedTask={setSelectedTask}
                selectedTask={selectedTask}
                showOnlyDiagram={Boolean(workflowID)}
                refresh={refresh}
                rightDrawerWidth={drawerWidth}
                deselectTaskAndCloseRightPanel={deselectTaskAndCloseRightPanel}
              />
            </div>
          ) : (!execution && isFetching) || (execution && isFetching) ? (
            <DefaultLoader
              displayModal={
                (!execution && isFetching) || (execution && isFetching)
              }
              message="Workflow is getting loaded"
            />
          ) : (
            <div className={`${classes.body} ${classes.flexBody}`}>
              No Execution Data
            </div>
          )}
        </>
      )}
      {selectedTask && (
        <div
          id={workflowID ? "right-drawer" : "slide-panel"}
          className={classes.drawer}>
          <div
            id="dragger"
            onMouseDown={(event) => handleMousedown(event)}
            onMouseUp={(event) => handleDragEnd(event)}
            className={classes.dragger}
          />
          <div className={classes.drawerMain}>
            <div className={classes.drawerHeader}>
              {isFullWidth ? (
                <Tooltip title="Restore sidebar">
                  <IconButton onClick={() => handleFullScreenExit()}>
                    <FullscreenExitIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Maximize sidebar">
                  <IconButton onClick={() => handleFullScreen()}>
                    <FullscreenIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Close sidebar">
                <IconButton onClick={() => handleClose()}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>
            <div className={classes.drawerContent}>
              <RightPanel
                className={classes.rightPanel}
                selectedTask={selectedTask}
                dag={dag}
                onTaskChange={setSelectedTask}
                view={view}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
